import React, { useState } from "react";
import CalculatorPage from "./CalculatorPage";

function LoanCalculator() {
  const [principal, setPrincipal] = useState("");
  const [rate, setRate] = useState("");
  const [years, setYears] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [funFact, setFunFact] = useState("");
  const [showFunFact, setShowFunFact] = useState(false);

  const onInput = (setter) => (e) => {
    setter(e.target.value);
    setResult(null);
    setShowFunFact(false);
    setError("");
  };

  const calculateEMI = () => {
    const p = parseFloat(principal);
    const annualRate = parseFloat(rate);
    const t = parseFloat(years);
    if (isNaN(p) || isNaN(annualRate) || isNaN(t) || p <= 0 || annualRate < 0 || t <= 0) {
      setError("Please enter a loan amount, interest rate, and tenure.");
      setResult(null);
      setShowFunFact(false);
      return;
    }

    const r = annualRate / 12 / 100;
    const n = t * 12;
    const emi =
      r === 0 ? p / n : (p * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
    const totalPayment = emi * n;

    setError("");
    setResult({
      emi: emi.toFixed(2),
      total: totalPayment.toFixed(2),
      interest: (totalPayment - p).toFixed(2),
    });

    const funFactsList = [
      "Paying a little extra each month can reduce your loan term significantly.",
      "A lower interest rate can save you thousands over the life of the loan.",
      "Shorter loan terms usually have lower interest rates.",
      "Interest rates can vary daily based on the economy.",
      "Making bi-weekly payments can reduce total interest paid.",
    ];

    setFunFact(funFactsList[Math.floor(Math.random() * funFactsList.length)]);
    setShowFunFact(true);
  };

  return (
    <CalculatorPage
      title="Loan EMI Calculator"
      metaDescription="Free loan EMI calculator: enter loan amount, interest rate, and tenure to get your monthly installment, total payment, and total interest."
      intro={
        <>
          Calculate your <strong>Equated Monthly Installment (EMI)</strong> —
          the fixed amount you pay each month — plus the total interest over
          the life of the loan.
        </>
      }
      seoContent={
        <>
          <h2>How EMI is calculated</h2>
          <p>
            This calculator uses the standard reducing-balance formula:
            EMI = P × r × (1 + r)<sup>n</sup> / ((1 + r)<sup>n</sup> − 1),
            where P is the loan amount, r the monthly interest rate (annual
            rate ÷ 12 ÷ 100), and n the number of monthly payments. Early
            payments are mostly interest; later payments are mostly principal.
          </p>
          <h2>Tenure vs. interest trade-off</h2>
          <p>
            A longer tenure lowers the monthly EMI but increases total
            interest paid — sometimes dramatically. Try the same loan at 15
            and 30 years and compare the "total interest" line: the monthly
            relief of the longer term usually costs far more overall. Results
            are estimates; your lender's exact figures may differ slightly due
            to fees and rounding.
          </p>
          <h2>Frequently asked questions</h2>
          <h3>What is an EMI?</h3>
          <p>
            EMI stands for Equated Monthly Installment — the fixed amount you
            pay your lender each month, covering both interest and principal,
            until the loan is fully repaid.
          </p>
          <h3>Does a longer tenure reduce my total cost?</h3>
          <p>
            No. A longer tenure lowers each monthly payment but increases the
            total interest you pay over the life of the loan. Compare the "total
            interest" figure across different tenures to see the trade-off.
          </p>
          <h3>Is this the same as my bank's EMI?</h3>
          <p>
            It uses the standard reducing-balance formula, so it's very close.
            Your bank's exact figure may differ slightly because of processing
            fees, insurance, or rounding rules.
          </p>
        </>
      }
    >
      <div className="fields">
        <label className="field">
          <span>Loan Amount</span>
          <input type="number" value={principal} onChange={onInput(setPrincipal)} placeholder="e.g. 250000" />
        </label>
        <label className="field">
          <span>Interest Rate (% per year)</span>
          <input type="number" value={rate} onChange={onInput(setRate)} placeholder="e.g. 7.5" />
        </label>
        <label className="field">
          <span>Loan Tenure (Years)</span>
          <input type="number" value={years} onChange={onInput(setYears)} placeholder="e.g. 20" />
        </label>
      </div>

      <button className="btn" onClick={calculateEMI}>Calculate EMI</button>

      {error && <div className="error">{error}</div>}

      {result && (
        <>
          <div className="result result-breakdown">
            <div>
              Monthly EMI: <strong>{Number(result.emi).toLocaleString()}</strong>
            </div>
            <div>
              Total payment: <strong>{Number(result.total).toLocaleString()}</strong>
            </div>
            <div>
              Total interest: <strong>{Number(result.interest).toLocaleString()}</strong>
            </div>
          </div>
          {showFunFact && (
            <div className="fun-fact fun-fact-animate">💡 {funFact}</div>
          )}
        </>
      )}
    </CalculatorPage>
  );
}

export default LoanCalculator;
